import { EventTypes } from '@racemap/sdk/schema/events';
import { memoize } from 'lodash';
import screenfull from 'screenfull';
import create from 'zustand';
import { combine } from 'zustand/middleware';
import type ChunkManager from './ChunkManager';
import { actions } from './actions';
import type { Tuple3, Tuple4 } from './constants';
import { getCurrentEvents, reduceAny } from './selectors';
import type TrackView from './utils/TrackView';

export enum MapType {
  SATELLITE = 'SATELLITE',
  TERRAIN = 'TERRAIN',
  HYBRID = 'HYBRID',
  ROADMAP = 'ROADMAP',
  DARK = 'DARK',
}
export enum UnitType {
  METRIC = 'METRIC',
  IMPERIAL = 'IMPERIAL',
}
export enum TimeMode {
  RELATIVE = 'RELATIVE',
  ABSOLUTE = 'ABSOLUTE',
}
export enum GeolocationStatus {
  DISABLED = 'DISABLED',
  OFF = 'OFF',
  SEARCHING = 'SEARCHING',
  FOLLOWING = 'FOLLOWING',
}

export type XY = {
  x: number;
  y: number;
};

export type LatLng = {
  lat: number;
  lng: number;
};

export type LatLngOpacity = {
  lat: number;
  lng: number;
  opacity: number;
};

export type Point = LatLng & {
  time: number;
  offset: number;
};

export type PointOpacity = LatLng & {
  time: number;
  opacity: number;
  offset: number;
};

export type Color = {
  hex: string;
  hexNum: number;
  isLight: boolean;
  tuple: Tuple4;
};

export enum InfoSheetTabs {
  INFO = 'INFO',
  ELEVATION = 'ELEVATION',
  FLOW = 'FLOW',
  STATS = 'STATS',
  TIMING = 'TIMING',
}

export enum PinToShadowTrackMode {
  NONE = 'NONE',
  MAGNETIC = 'MAGNETIC',
  FORCED = 'FORCED',
}

export type StarterType = {
  readonly type: 'STARTER';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name?: string;
  readonly startNumber?: string;
  readonly searchText: string;
  readonly markerColor: Color;
  readonly deviceClass: string;
  readonly tags: Map<string, string>;
};

export type PoiType = {
  readonly type: 'POI';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name: string;
  readonly shortName?: string;
  readonly icon?: string;
  readonly markerColor: Color;
  readonly latlng: LatLng;
  readonly offset?: number;
};

export type TrackType = {
  readonly type: 'TRACK';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly name: string;
  readonly markerColor: Color;
  readonly coordinates: Array<Tuple3>;
};

export type ExternalLayerType = {
  readonly type: 'EXTERNAL_LAYER';
  readonly index: number;
  readonly id: string;
  readonly eventIndex: number;
  readonly layerType: 'wms';
  readonly tiles: Array<string>;
  readonly tileSize: number;
};

export type StarterMap = Map<string, Array<StarterType>>;

export enum FlagContent {
  STARTNUMBER = 'STARTNUMBER',
  NAME = 'NAME',
  STARTNUMBER_AND_NAME = 'STARTNUMBER_AND_NAME',
  ONE_LETTER_NAMES = 'ONE_LETTER_NAMES',
  TWO_LETTER_NAMES = 'TWO_LETTER_NAMES',
}

export type EventState = {
  readonly eventId: string;
  readonly index: number;
  readonly name: string;
  readonly shortName: string;
  readonly slug: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly eventDescription: string;
  readonly eventWebsite: string;
  readonly eventContactMail: string;
  readonly eventLocation: string;
  readonly starters: Array<StarterType>;
  readonly pois: Array<PoiType>;
  readonly tracks: Array<TrackType>;
  readonly externalLayers: Array<ExternalLayerType>;
  readonly chunkedShadowTrack: TrackView | null;
  readonly shadowTrackIsVisible: boolean;
  readonly modules: Modules;
  readonly options: {
    readonly markerTimeout: number;
    readonly stepWidth: number;
    readonly flagContent: FlagContent;
    readonly selectedFlagContent: FlagContent;
    readonly liveDelay: number;
    readonly pollingInterval: number;
    readonly replaySpeedup: number;
    readonly tailTimeout: number;
    readonly elevationChartOptions: null | {
      readonly min: null | number;
      readonly max: null | number;
    };
    readonly listOpenByDefault: boolean;
    readonly infoTabs: {
      readonly openByDefault: boolean;
      readonly defaultTab: InfoSheetTabs;
      readonly disabledTabs: Array<InfoSheetTabs>;
    };
    readonly viewport: null | {
      readonly latitude: number;
      readonly longitude: number;
      readonly zoom: number;
      readonly pitch: number;
      readonly bearing: number;
    };
    readonly filterTagKeys: Array<string> | null;
    readonly visibleStarterGroups: Array<string>;
    readonly showDistanceMarkers: boolean;
  };
};

export type EventChangeableState = {
  readonly eventId: string;
  readonly chunkManager: ChunkManager;
  readonly starterTracks: Map<string, Array<Point>>;
};

export type ChunkInfo = {
  readonly id: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly compressedBuffer: ArrayBuffer;
  readonly encodedLength: number;
  readonly decodedLength: number;
};

export type TimerState = {
  readonly speed: number;
  readonly time: number;
  readonly isLive: boolean;
  readonly isPlaying: boolean;
  readonly selected: Array<string>;
  readonly selectedTags: Map<string, Array<string>>;
  readonly hidden: Set<string>;
  readonly highlighted: string | null;
  readonly highlightedOffset: number;
  readonly geolocation: {
    readonly status: GeolocationStatus;
    readonly latlng: null | LatLngOpacity;
  };
};

export type Images = {
  readonly logo: string;
  readonly original: string;
};

export type SponsorLogo = {
  readonly id: string;
  readonly url: string;
  readonly title: string;
  readonly imageUrls: Images | null;
  readonly altText: string;
};

export type SponsorModule =
  | {
      readonly enabled: false;
    }
  | {
      readonly enabled: true;
      readonly alignment: 'left' | 'center' | 'right' | 'space-between' | 'space-around';
      readonly logos: Array<SponsorLogo> | null;
    };

export type TimingModule =
  | { readonly enabled: false }
  | { readonly enabled: true; readonly leaderboardUrl: string };
export type NetReplayModule =
  | { readonly enabled: false }
  | {
      readonly enabled: true;
      readonly syncOffset: number;
      readonly syncTime: Date;
      readonly sliderStartTime: Date;
      readonly sliderEndTime: Date;
    };

export type Modules = {
  readonly timing: TimingModule;
  readonly sponsor: SponsorModule;
  readonly netReplay: NetReplayModule;
  readonly projection: { enabled: boolean };
};

export type OptionsState = {
  readonly type: EventTypes;
  readonly mapType: MapType;
  readonly fullscreenButton: boolean;
  readonly showAllFlags: boolean;
  readonly overrideFlagContent: null | FlagContent;
  readonly overrideSelectedFlagContent: null | FlagContent;
  readonly largeMode: boolean;
  readonly overrideZoom: null | number;
  readonly showProgressOnHeightChart: boolean;

  readonly unitType: UnitType;
  readonly timeMode: TimeMode;
  readonly pinToShadowTrack: PinToShadowTrackMode;
};

export type State = {
  readonly eventId: string;
  readonly currentEventIndices: Array<number>;
  readonly events: Array<EventState>;
  readonly eventsChangeable: Array<EventChangeableState>;
  readonly startersById: StarterMap;
  readonly timer: TimerState;
  readonly options: OptionsState;
  readonly actions: ReturnType<typeof actions>;
};

export const useStore = create<State>(
  combine(
    {
      eventId: '',
      currentEventIndices: [],
      events: [],
      eventsChangeable: [],
      startersById: new Map(),
      timer: {
        speed: 1,
        time: 0,
        isLive: false,
        isPlaying: false,
        selected: [],
        selectedTags: new Map(),
        hidden: new Set(),
        highlighted: null,
        highlightedOffset: -1,
        geolocation: {
          status:
            typeof window !== 'undefined' && 'geolocation' in window.navigator
              ? GeolocationStatus.OFF
              : GeolocationStatus.DISABLED,
          latlng: null,
        },
      },
      options: {
        type: EventTypes.REGULAR,
        fullscreenButton: screenfull.isEnabled,
        mapType: MapType.TERRAIN,
        unitType: UnitType.METRIC,
        timeMode: TimeMode.RELATIVE,
        largeMode: false,
        overrideZoom: null,
        showAllFlags: false,
        showProgressOnHeightChart: false,
        overrideFlagContent: null,
        overrideSelectedFlagContent: null,
        pinToShadowTrack: PinToShadowTrackMode.NONE,
      },
    } as Omit<State, 'actions'>,
    memoize((set) => ({
      actions: actions(set),
    })),
  ),
);

export const useSponsorModule = () =>
  useStore((state) => reduceAny(getCurrentEvents(state), (a) => a.modules.sponsor));
